import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Calendar, CreditCard, LogOut, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = '';

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({
    tutor_id: '',
    subject: 'French',
    date: '',
    time: '',
    duration: 60,
    price: 60.0,
    session_type: 'online'
  });
  const [showZoomSuccess, setShowZoomSuccess] = useState(false);
  const [newSessionZoomLink, setNewSessionZoomLink] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, tutorsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/sessions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tutors`, { withCredentials: true })
      ]);
      setSessions(sessionsRes.data);
      setTutors(tutorsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookSession = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/sessions`,
        { ...bookingData, student_id: user._id },
        { withCredentials: true }
      );
      
      if (response.data.session_type === 'online' && response.data.zoom_link) {
        setNewSessionZoomLink(response.data.zoom_link);
        setShowZoomSuccess(true);
      }
      
      setShowBooking(false);
      fetchData();
    } catch (error) {
      console.error('Error booking session:', error);
      alert('Failed to book session');
    }
  };

  const handlePayment = async (session) => {
    const durationToPackage = {
      30: 'session_30',
      45: 'session_45',
      60: 'session_60',
      90: 'session_90'
    };
    
    const packageId = durationToPackage[session.duration] || 'session_60';
    
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/checkout`,
        {
          package_id: packageId,
          origin_url: window.location.origin,
          session_id: session.id
        },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to initiate payment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold">Welcome, {user.name}</span>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-sm font-semibold px-4 py-2 border border-slate-900 rounded-md hover:bg-slate-900 hover:text-white"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-black tracking-tight mb-2">Student Dashboard</h1>
          <p className="text-slate-600">Manage your tutoring sessions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 border border-slate-200">
            <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-2">Total Sessions</div>
            <div className="text-3xl font-black">{sessions.length}</div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-2">Pending</div>
            <div className="text-3xl font-black">{sessions.filter(s => s.status === 'pending').length}</div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-2">Confirmed</div>
            <div className="text-3xl font-black">{sessions.filter(s => s.status === 'confirmed').length}</div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <button
              onClick={() => setShowBooking(!showBooking)}
              className="w-full py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
              data-testid="book-session-button"
            >
              Book New Session
            </button>
          </div>
        </div>

        {showBooking && (
          <div className="bg-white p-8 border border-slate-200 mb-8">
            <h2 className="text-xl font-bold mb-6">Book a Session</h2>
            <form onSubmit={handleBookSession} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Tutor</label>
                <select
                  value={bookingData.tutor_id}
                  onChange={(e) => setBookingData({ ...bookingData, tutor_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md"
                  required
                  data-testid="booking-tutor-select"
                >
                  <option value="">Select a tutor</option>
                  {tutors.map(tutor => (
                    <option key={tutor._id} value={tutor._id}>{tutor.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Session Type</label>
                <select
                  value={bookingData.session_type}
                  onChange={(e) => setBookingData({ ...bookingData, session_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md"
                  data-testid="booking-session-type-select"
                >
                  <option value="online">Online (via Zoom)</option>
                  <option value="in-person">In-Person</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Subject</label>
                <select
                  value={bookingData.subject}
                  onChange={(e) => setBookingData({ ...bookingData, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md"
                  data-testid="booking-subject-select"
                >
                  <optgroup label="K-12 (Grades 1-12)">
                    <option value="French">French</option>
                    <option value="English">English</option>
                    <option value="Math">Math</option>
                  </optgroup>
                  <optgroup label="Adults">
                    <option value="ESL">ESL (English as a Second Language)</option>
                    <option value="FSL">FSL (French as a Second Language)</option>
                    <option value="Creole">Creole as a Second Language</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Duration & Price</label>
                <select
                  value={bookingData.duration}
                  onChange={(e) => {
                    const duration = parseInt(e.target.value);
                    const prices = { 30: 40, 45: 55, 60: 60, 90: 90 };
                    setBookingData({ ...bookingData, duration, price: prices[duration] });
                  }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md"
                  data-testid="booking-duration-select"
                >
                  <option value="30">30 Minutes - $40</option>
                  <option value="45">45 Minutes - $55</option>
                  <option value="60">60 Minutes - $60</option>
                  <option value="90">90 Minutes - $90</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Date</label>
                <input
                  type="date"
                  value={bookingData.date}
                  onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md"
                  required
                  data-testid="booking-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Time</label>
                <input
                  type="time"
                  value={bookingData.time}
                  onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-md"
                  required
                  data-testid="booking-time-input"
                />
              </div>
              <div className="md:col-span-2 flex gap-4">
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
                  data-testid="booking-submit-button"
                >
                  Book Session
                </button>
                <button
                  type="button"
                  onClick={() => setShowBooking(false)}
                  className="px-6 py-2 border border-slate-900 font-bold rounded-md hover:bg-slate-900 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {showZoomSuccess && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowZoomSuccess(false)}>
            <div className="bg-white p-8 border-2 border-[#002FA7] rounded-md max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-black mb-2">Session Booked!</h3>
                <p className="text-slate-600 mb-4">Your online session has been scheduled.</p>
                <div className="bg-[#F3F4F6] p-4 border border-slate-200 rounded-md mb-4">
                  <div className="text-sm font-semibold mb-2">Join Zoom Meeting:</div>
                  <a 
                    href={newSessionZoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#002FA7] font-semibold hover:underline break-all text-sm"
                    data-testid="zoom-link"
                  >
                    {newSessionZoomLink}
                  </a>
                </div>
                <p className="text-xs text-slate-600 mb-4">You can also find this link in your sessions table below.</p>
                <button
                  onClick={() => setShowZoomSuccess(false)}
                  className="w-full py-2 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold">My Sessions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Zoom Link</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-slate-600">
                      No sessions yet. Book your first session above!
                    </td>
                  </tr>
                ) : (
                  sessions.map(session => (
                    <tr key={session.id} className="border-b border-slate-200">
                      <td className="px-6 py-4">{session.subject}</td>
                      <td className="px-6 py-4">{session.date}</td>
                      <td className="px-6 py-4">{session.time}</td>
                      <td className="px-6 py-4">{session.duration} min</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                          session.session_type === 'online' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {session.session_type === 'online' ? 'Online' : 'In-Person'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {session.zoom_link ? (
                          <div className="flex flex-col gap-1">
                            <a 
                              href={session.zoom_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#002FA7] font-semibold hover:underline text-sm"
                              data-testid="session-zoom-link"
                            >
                              Join Zoom
                            </a>
                            <button
                              onClick={async () => {
                                try {
                                  const { data } = await axios.get(`${BACKEND_URL}/api/sessions/${session.id}/calendar`, { withCredentials: true });
                                  window.open(data.google_calendar_url, '_blank');
                                } catch (error) {
                                  console.error('Error getting calendar link:', error);
                                }
                              }}
                              className="text-xs text-slate-600 hover:text-[#002FA7] hover:underline"
                            >
                              + Add to Calendar
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">${session.price}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded ${
                          session.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          session.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {session.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {session.status === 'pending' && !session.paid && (
                          <button
                            onClick={() => handlePayment(session)}
                            className="text-sm font-semibold text-[#002FA7] hover:underline"
                            data-testid="pay-session-button"
                          >
                            Pay Now
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
